// Copyright 2026 TokenLat. Licensed under the Apache License, Version 2.0.
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@coze-arch/coze-design';

import {
  AccountNotice,
  PlatformLink,
  KeyBudget,
  legacyAmount,
  t,
} from './tokenlat-ui';
import { GroupLabel } from './tokenlat-selectors';
import { NewKey } from './tokenlat-create';
import {
  accountConfig,
  AccountError,
  accountRequest,
  formatCredits,
  keyAvailable,
  type AccountConfig,
  type AccountKey,
  type AccountOptions,
  type KeyPage,
  type AccountBalance,
} from './tokenlat-api';

import styles from './tokenlat-account.module.less';
function useKeyPage() {
  const [config, setConfig] = useState<AccountConfig>();
  const [options, setOptions] = useState<AccountOptions>();
  const [data, setData] = useState<KeyPage>();
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<unknown>();
  const [adding, setAdding] = useState(false);
  const active = useRef<AbortController>();
  const mutation = useRef(false);

  const refresh = useCallback(async () => {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    setError(undefined);
    try {
      const cfg = await accountConfig(controller.signal);
      setConfig(cfg);
      const [keys, opts] = await Promise.all([
        accountRequest<KeyPage>(`/keys?page=${page}&size=20`, {
          signal: controller.signal,
        }),
        accountRequest<AccountOptions>('/options', {
          signal: controller.signal,
        }),
      ]);
      if (controller.signal.aborted) {
        return;
      }
      if (keys.billing_mode !== opts.billing_mode) {
        throw new AccountError('account_unavailable');
      }
      setData(keys);
      setOptions(opts);
    } catch (e) {
      if (!controller.signal.aborted) {
        setData(undefined);
        setError(e);
      }
    } finally {
      if (!controller.signal.aborted) {
        setBusy(false);
      }
    }
  }, [page]);

  useEffect(() => {
    void refresh();
    const focus = () => {
      if (!mutation.current) {
        void refresh();
      }
    };
    window.addEventListener('focus', focus);
    return () => {
      active.current?.abort();
      window.removeEventListener('focus', focus);
    };
  }, [refresh]);

  const select = async (id: number) => {
    if (mutation.current) {
      return;
    }
    mutation.current = true;
    setMutating(true);
    setError(undefined);
    try {
      await accountRequest('/keys/default', {
        method: id ? 'PUT' : 'DELETE',
        body: id ? { token_id: id } : undefined,
      });
      await refresh();
    } catch (e) {
      setError(e);
    } finally {
      mutation.current = false;
      setMutating(false);
    }
  };

  return {
    config,
    options,
    data,
    page,
    setPage,
    busy,
    mutating,
    error,
    adding,
    setAdding,
    refresh,
    select,
  };
}
function KeyRow({
  item,
  options,
  defaultTokenID,
  disabled,
  select,
}: {
  item: AccountKey;
  options: AccountOptions;
  defaultTokenID: number;
  disabled: boolean;
  select: (id: number) => Promise<void>;
}) {
  return (
    <tr key={item.id} data-selected={item.id === defaultTokenID}>
      <td>
        <strong>{item.name}</strong>
        <div className={styles['key-id']}>ID {item.id}</div>
        <div className={styles.muted}>{item.mask}</div>
        <details className={styles['key-details']}>
          <summary>{t('key_details')}</summary>
          <dl>
            <dt>{t('models')}</dt>
            <dd>
              {item.model_limits_enabled
                ? item.model_limits.split(',').join(', ')
                : t('all_models')}
            </dd>
            <dt>{t('ips')}</dt>
            <dd>
              {item.allow_ips === undefined
                ? '—'
                : item.allow_ips || t('no_ip_limit')}
            </dd>
            <dt>{t('created_at')}</dt>
            <dd>
              {item.created_time
                ? new Date(item.created_time * 1000).toLocaleString()
                : '—'}
            </dd>
            <dt>{t('last_used')}</dt>
            <dd>
              {item.accessed_time
                ? new Date(item.accessed_time * 1000).toLocaleString()
                : '—'}
            </dd>
            {item.group === 'auto' ? (
              <>
                <dt>{t('cross_group')}</dt>
                <dd>
                  {item.cross_group_retry === undefined
                    ? '—'
                    : t(item.cross_group_retry ? 'enabled' : 'disabled')}
                </dd>
              </>
            ) : null}
          </dl>
        </details>
      </td>
      <td>
        <span
          className={styles['status-badge']}
          data-available={keyAvailable(item)}
        >
          {t(`status_${item.status}`)}
          {item.status === 1 &&
          item.expired_time !== -1 &&
          item.expired_time * 1000 <= Date.now()
            ? ` · ${t('status_3')}`
            : ''}
        </span>
      </td>
      <td>
        <GroupLabel value={item.group} options={options} />
      </td>
      <td>
        <KeyBudget item={item} options={options} />
      </td>
      <td>
        {item.expired_time === -1
          ? t('never')
          : new Date(item.expired_time * 1000).toLocaleString()}
      </td>
      <td>
        {item.id === defaultTokenID ? (
          <span className={styles['selected-badge']}>
            {t('selected')}
            {!keyAvailable(item) ? ` · ${t('unavailable')}` : ''}
          </span>
        ) : (
          <Button
            disabled={disabled || !keyAvailable(item)}
            onClick={() => {
              void select(item.id);
            }}
          >
            {t('select')}
          </Button>
        )}
      </td>
    </tr>
  );
}
export function TokenLatKeysPanel() {
  const {
    config,
    options,
    data,
    page,
    setPage,
    busy,
    mutating,
    error,
    adding,
    setAdding,
    refresh,
    select,
  } = useKeyPage();
  return (
    <section
      data-tokenlat-account
      className={styles.panel}
      aria-busy={busy || mutating}
    >
      <p>{t('default_hint')}</p>
      <div className={styles.toolbar}>
        <Button
          loading={busy}
          disabled={busy || mutating}
          onClick={() => {
            void refresh();
          }}
        >
          {t('refresh')}
        </Button>
        <Button
          disabled={!options || !data || busy || mutating}
          onClick={() => setAdding(true)}
        >
          {t('create')}
        </Button>
        <PlatformLink config={config} path="/keys">
          {t('manage_delete')}
        </PlatformLink>
      </div>
      <AccountNotice error={error} />
      {data && options ? (
        <>
          <div className={styles['default-summary']}>
            <span>
              {data.default_token_id
                ? `${t('default')}: ${data.items.find(item => item.id === data.default_token_id)?.name ?? ''} · ID ${data.default_token_id}`
                : t('no_default')}
            </span>
            {data.default_token_id ? (
              <Button
                disabled={busy || mutating}
                onClick={() => {
                  void select(0);
                }}
              >
                {t('clear_default')}
              </Button>
            ) : null}
          </div>
          <div className={styles['table-wrap']}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('name')}</th>
                  <th>{t('status')}</th>
                  <th>{t('group')}</th>
                  <th>{t('key_budget')}</th>
                  <th>{t('expiry')}</th>
                  <th>{t('default')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(item => (
                  <KeyRow
                    key={item.id}
                    item={item}
                    options={options}
                    defaultTokenID={data.default_token_id}
                    disabled={busy || mutating}
                    select={select}
                  />
                ))}
                {!data.items.length ? (
                  <tr>
                    <td colSpan={6}>{t('empty')}</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <div className={styles.toolbar}>
            <Button
              disabled={busy || mutating || page <= 1}
              onClick={() => setPage(page - 1)}
            >
              {t('previous')}
            </Button>
            <span>
              {page} / {Math.max(1, Math.ceil(data.total / data.size))} ·{' '}
              {t('total_keys')}: {data.total}
            </span>
            <Button
              disabled={busy || mutating || page * data.size >= data.total}
              onClick={() => setPage(page + 1)}
            >
              {t('next')}
            </Button>
          </div>
          <p className={styles.muted}>{t('budget_hint')}</p>
        </>
      ) : null}
      {adding && options ? (
        <NewKey
          options={options}
          onClose={() => setAdding(false)}
          onCreated={() => {
            void refresh();
          }}
        />
      ) : null}
    </section>
  );
}

export function TokenLatBillingPanel() {
  const [config, setConfig] = useState<AccountConfig>();
  const [balance, setBalance] = useState<AccountBalance>();
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const active = useRef<AbortController>();
  const refresh = useCallback(async () => {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    setError(undefined);
    setBalance(undefined);
    try {
      const cfg = await accountConfig(controller.signal);
      setConfig(cfg);
      const data = await accountRequest<AccountBalance>('/balance', {
        signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        setBalance(data);
      }
    } catch (e) {
      if (!controller.signal.aborted) {
        setError(e);
      }
    } finally {
      if (!controller.signal.aborted) {
        setBusy(false);
      }
    }
  }, []);
  useEffect(() => {
    void refresh();
    const focus = () => {
      void refresh();
    };
    window.addEventListener('focus', focus);
    return () => {
      active.current?.abort();
      window.removeEventListener('focus', focus);
    };
  }, [refresh]);
  const credits = balance?.credits;
  return (
    <section data-tokenlat-account className={styles.panel} aria-busy={busy}>
      <div className={styles.toolbar}>
        <Button
          loading={busy}
          disabled={busy}
          onClick={() => {
            void refresh();
          }}
        >
          {t('refresh')}
        </Button>
        <PlatformLink config={config} path="/wallet">
          {t('wallet')}
        </PlatformLink>
        <PlatformLink config={config} path="/usage-logs">
          {t('usage_details')}
        </PlatformLink>
      </div>
      <AccountNotice error={error} />
      {balance ? (
        <>
          <div className={styles.balance}>
            <span>
              {balance.billing_mode === 'unified'
                ? t('available_credits')
                : t('legacy_balance')}
            </span>
            <strong>
              {balance.billing_mode === 'unified'
                ? credits
                  ? `${formatCredits(credits.availableUnits, credits.scale)} ${t('credits')}`
                  : '—'
                : legacyAmount(balance)}
            </strong>
          </div>
          {balance.billing_mode === 'unified' && credits ? (
            <>
              <p>
                {t('processing')}:{' '}
                {formatCredits(credits.processingUnits, credits.scale)} ·{' '}
                {t('scheduled')}:{' '}
                {formatCredits(credits.scheduledUnits, credits.scale)}
              </p>
              {credits.nearestExpiry ? (
                <p>
                  {t('nearest_expiry')}:{' '}
                  {formatCredits(credits.nearestExpiryUnits, credits.scale)} ·{' '}
                  {new Date(credits.nearestExpiry).toLocaleString()}
                </p>
              ) : null}
              {credits.ownershipState !== 'active' ||
              credits.ledgerState !== 'active' ? (
                <p role="status">{t('account_frozen')}</p>
              ) : null}
            </>
          ) : null}
          <p className={styles.muted}>
            {t('as_of')}: {new Date(balance.fetched_at).toLocaleString()}
          </p>
        </>
      ) : null}
      <p className={styles.muted}>{t('balance_hint')}</p>
    </section>
  );
}
